import { ZfDropdown, ZfDropdownDivider, ZfDropdownItem, ZfFormField, ZfIcon, ZfInput, ZfRoadmapItem, ZfSelectOption, ZfStack } from '@zafire/react';
import moment from 'moment';
import type { FunctionComponent } from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { EventStatus } from '../../../api/generated/client';
import { DEFAULT_INPUT_DATE_FORMAT } from '../../../config/constants';
import { useShadowRootCustomStyles } from '../../../hooks/useShadowRootCustomStyles';
import type { RoadmapEvent } from '../../../interfaces/event';
import { getRoadmapEntryDisplayName, isRefinanced } from '../../../utils/eventUtils';
import { formatAmountCurrencyFirst } from '../../../utils/renderUtils';
import { getRoadmapEventIcon } from '../../icons/iconUtils';
import { useEventApiRequest } from '../../modal/eventModal/useEventApiRequest';

interface IProps {
    item: RoadmapEvent;
    index: string;
    timeValue: string;
    iconPosition: 'top' | 'middle' | 'bottom';
    showCurrentDateDivider: boolean;
    onEditClick: (roadmapEvent: RoadmapEvent) => void;
    onDeleteClick: (roadmapEvent: RoadmapEvent) => void;
}

export const RoadmapListItem: FunctionComponent<IProps> = ({
    item,
    index,
    timeValue,
    iconPosition,
    showCurrentDateDivider,
    onEditClick,
    onDeleteClick,
}: IProps) => {
    const { t, i18n } = useTranslation();
    const { updateEvent } = useEventApiRequest();

    const roadmapItemRef = useRef<HTMLZfRoadmapItemElement>(null);

    useShadowRootCustomStyles(roadmapItemRef, '.roadmap-item__time-value { max-width: 60px !important; }');

    const isPastEvent = moment(item.dueDate).isBefore(moment().startOf('day'));
    const isDisabledEvent = (roadmapEvent: RoadmapEvent) => ['COMPLETED_STOPPED', 'COMPLETED_SUCCESSFULLY'].includes(roadmapEvent.status!);

    const amount = item.signedReferenceAmount ? formatAmountCurrencyFirst(item.signedReferenceAmount, !isRefinanced(item), item.referenceCurrency, false) : '';

    return (
        <>
            {showCurrentDateDivider && <div style={{ paddingTop: '34px', paddingLeft: '90px' }}>{t('upcomingEvents')}</div>}
            <ZfRoadmapItem
                ref={roadmapItemRef}
                key={index}
                itemTitle={`${item.topicName?.[i18n.language]} - ${getRoadmapEntryDisplayName(item, t('refinancedLabel'))}`}
                description={moment(item.dueDate).format(DEFAULT_INPUT_DATE_FORMAT)}
                category={getRoadmapEventIcon(item.topicKey, item.eventType)}
                trailingText={amount}
                position={iconPosition}
                state={isDisabledEvent(item) ? 'disabled' : 'default'}
                timeValue={timeValue}
                negative={!!item.signedReferenceAmount && item.signedReferenceAmount < 0 && !isRefinanced(item)}
                readOnly={isPastEvent}
            >
                <ZfStack direction="row" alignment="center">
                    <ZfFormField style={{ width: '200px', position: 'absolute', right: '200px' }}>
                        <ZfInput type="select" value={item.status} onSelectChange={(event) => updateEvent({ ...item, status: event.detail as EventStatus })}>
                            {['DEFINED', 'IN_PROGRESS', 'COMPLETED_SUCCESSFULLY', 'COMPLETED_STOPPED'].map((option) => (
                                <ZfSelectOption key={option} value={option} description={t(`eventStatus-${option}`)} />
                            ))}
                        </ZfInput>
                    </ZfFormField>
                    <ZfDropdown icon="more_vert" hierarchy="tertiary">
                        <ZfDropdownItem label={t('modalEditButton')} onDropdownItemSelect={() => onEditClick(item)}>
                            <ZfIcon slot="leading" name="edit" size="medium" />
                        </ZfDropdownItem>
                        <ZfDropdownDivider />
                        <ZfDropdownItem
                            label={t('modalDeleteButton')}
                            variant="destructive"
                            onDropdownItemSelect={() => onDeleteClick(item)}
                            state={item.eventType == 'MATURITY' ? 'disabled' : 'default'}
                        >
                            <ZfIcon slot="leading" name="delete" size="medium" />
                        </ZfDropdownItem>
                    </ZfDropdown>
                </ZfStack>
            </ZfRoadmapItem>
        </>
    );
};
